import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import NavLink from '../common/NavLink';
import { getIdToken } from '../Firebase';
import { slugify } from '../../utils/slugify';

const modes = ['EVERY_MATCH', 'SELECTIVE', 'DAILY_SUMMARY', 'OFF'];

const authHeaders = async () => {
  const token = await getIdToken(false);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const hasGroup = (group) => {
  const value = (group || '').toString().trim();
  return value && value.toLowerCase() !== 'null' && value !== 'Brak';
};

const leagueLabel = (row) => (
  hasGroup(row.leagueGroup)
    ? `${row.competitionName} (${row.leagueGroup.trim()})`
    : row.competitionName
);

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const modeColor = (mode) => {
  if (mode === 'EVERY_MATCH') return 'success';
  if (mode === 'SELECTIVE') return 'info';
  if (mode === 'DAILY_SUMMARY') return 'warning';
  return 'default';
};

const parsePayload = (value) => {
  if (!value) return {};
  try {
    return typeof value === 'string' ? JSON.parse(value) : value;
  } catch (err) {
    return {};
  }
};

const watchScoreTitle = (row) => {
  const payload = parsePayload(row.payloadJson);
  const base = payload.scoreBase ?? '?';
  const level = payload.scoreLevelBonus ?? '?';
  const rank = payload.scoreRankBonus ?? '?';
  const type = payload.scoreTypeBonus ?? '?';
  return `score = base ${base} + bonus ligi ${level} + bonus ranku wybryku ${rank} + bonus typu ${type}`;
};

const SocialPlanManager = () => {
  const [date, setDate] = useState(todayIso());
  const [plan, setPlan] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async (selectedDate = date) => {
    setLoading(true);
    setError('');
    try {
      const headers = await authHeaders();
      const [planResponse, candidatesResponse, watchlistResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/social-plan`, {
          params: { date: selectedDate },
          headers,
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/social-plan/candidates`, {
          params: { date: selectedDate },
          headers,
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/social-plan/watchlist`, {
          params: { date: selectedDate },
          headers,
        }),
      ]);
      setPlan(Array.isArray(planResponse.data?.plan) ? planResponse.data.plan : []);
      setCandidates(Array.isArray(candidatesResponse.data?.candidates) ? candidatesResponse.data.candidates : []);
      setWatchlist(Array.isArray(watchlistResponse.data?.watchlist) ? watchlistResponse.data.watchlist : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load(date);
  }, [date, load]);

  const generatePlan = async () => {
    setActionLoading(true);
    setError('');
    setMessage('');
    try {
      const headers = await authHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/social-plan/generate`,
        { date },
        { headers }
      );
      setMessage(`Wygenerowano plan: ${response.data?.plannedLeagues || 0} lig, aktywne: ${response.data?.activeLeagues || 0}, watchlista: ${response.data?.watchlistedMatches || 0}, kandydaci z rozegranych: ${response.data?.endedMatchCandidates || 0}.`);
      await load(date);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const evaluateEnded = async () => {
    setActionLoading(true);
    setError('');
    setMessage('');
    try {
      const headers = await authHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/social-plan/evaluate-ended`,
        { date },
        { headers }
      );
      setMessage(`Utworzono kandydatów: ${response.data?.createdCandidates || 0}.`);
      await load(date);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const updatePlanMode = async (row, publishMode) => {
    setActionLoading(true);
    setError('');
    setMessage('');
    try {
      const headers = await authHeaders();
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/social-plan/${row.id}`,
        { publishMode },
        { headers }
      );
      setPlan((current) => current.map((item) => (
        item.id === row.id ? { ...item, publishMode, source: 'manual' } : item
      )));
      setMessage(`Zmieniono tryb: ${leagueLabel(row)} -> ${publishMode}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const renderLeague = (row) => {
    const label = leagueLabel(row);
    return (
      <Stack spacing={0.25}>
        <Typography component={NavLink} to={`/liga/${row.competitionId}/${slugify(label)}`} sx={{ color: '#90caf9', fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ID {row.competitionId} | level {row.level}
        </Typography>
      </Stack>
    );
  };

  const renderTeam = (teamId, name) => {
    if (!name) return '-';
    if (!teamId) return name;
    return (
      <Typography component={NavLink} to={`/team/${teamId}/${slugify(name)}`} sx={{ color: '#90caf9', fontWeight: 600 }}>
        {name}
      </Typography>
    );
  };

  const renderMatch = (row, withScore = false) => {
    if (!row.matchId) return '-';
    return (
      <Stack spacing={0.25}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
          {renderTeam(row.homeTeamId, row.homeTeam)}
          <Typography component="span" color="text.secondary">-</Typography>
          {renderTeam(row.awayTeamId, row.awayTeam)}
        </Stack>
        {withScore && row.homeGoals !== null && row.homeGoals !== undefined && (
          <Typography variant="caption" color="text.secondary">
            {row.homeGoals}:{row.awayGoals}
          </Typography>
        )}
      </Stack>
    );
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>Ladowanie...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
            Plan social media
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dzienne tryby publikacji, kandydaci i mecze obserwowane statystycznie.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            type="date"
            size="small"
            label="Data"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" onClick={() => load(date)} disabled={actionLoading}>
            Odśwież
          </Button>
          <Button variant="contained" onClick={generatePlan} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={18} /> : 'Generuj plan'}
          </Button>
          <Button variant="outlined" onClick={evaluateEnded} disabled={actionLoading || plan.length === 0}>
            Oceń zakończone
          </Button>
        </Stack>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Chip size="small" label={`Ligi: ${plan.length}`} />
        <Chip size="small" label={`Kandydaci: ${candidates.length}`} />
        <Chip size="small" label={`Watchlista: ${watchlist.length}`} />
      </Stack>

      <Paper sx={{ overflow: 'auto', bgcolor: '#1e1e1e', mb: 3 }}>
        <Table size="small" sx={{ minWidth: 1320 }}>
          <TableHead>
            <TableRow>
              <TableCell>Liga</TableCell>
              <TableCell>Mecze</TableCell>
              <TableCell>Tryb</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Źródło</TableCell>
              <TableCell>Kandydaci</TableCell>
              <TableCell>Watchlista</TableCell>
              <TableCell>Notatka</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plan.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Brak planu dla wybranej daty.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : plan.map((row) => (
              <TableRow hover key={row.id}>
                <TableCell>{renderLeague(row)}</TableCell>
                <TableCell>{row.endedCount}/{row.matchCount}</TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel>Tryb</InputLabel>
                    <Select
                      value={row.publishMode}
                      label="Tryb"
                      disabled={actionLoading}
                      onChange={(event) => updatePlanMode(row, event.target.value)}
                    >
                      {modes.map((mode) => (
                        <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Chip size="small" color={modeColor(row.publishMode)} label={row.publishMode} />
                </TableCell>
                <TableCell>{row.source}</TableCell>
                <TableCell>{row.candidateCount}</TableCell>
                <TableCell>{row.watchlistCount}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {row.editorialNote || '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        Kandydaci
      </Typography>
      <Paper sx={{ overflow: 'auto', bgcolor: '#1e1e1e', mb: 3 }}>
        <Table size="small" sx={{ minWidth: 1180 }}>
          <TableHead>
            <TableRow>
              <TableCell>Score</TableCell>
              <TableCell>Typ</TableCell>
              <TableCell>Tytuł</TableCell>
              <TableCell>Liga</TableCell>
              <TableCell>Mecz</TableCell>
              <TableCell>Powód</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {candidates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Brak kandydatów.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : candidates.map((row) => (
              <TableRow hover key={row.id}>
                <TableCell>
                  <Chip size="small" label={row.score} color={row.score >= 80 ? 'success' : 'default'} />
                </TableCell>
                <TableCell>{row.topicType}</TableCell>
                <TableCell>{row.title}</TableCell>
                <TableCell>{renderLeague(row)}</TableCell>
                <TableCell>
                  {renderMatch(row, true)}
                </TableCell>
                <TableCell>{row.reason}</TableCell>
                <TableCell>{row.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        Watchlista
      </Typography>
      <Paper sx={{ overflow: 'auto', bgcolor: '#1e1e1e' }}>
        <Table size="small" sx={{ minWidth: 1500 }}>
          <TableHead>
            <TableRow>
              <TableCell>Waga</TableCell>
              <TableCell>Typ</TableCell>
              <TableCell>Druzyna</TableCell>
              <TableCell>Rank</TableCell>
              <TableCell>Wynik</TableCell>
              <TableCell>Lider</TableCell>
              <TableCell>Mecz</TableCell>
              <TableCell>Liga</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Opis</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {watchlist.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Brak meczów na watchliście.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : watchlist.map((row) => (
              <TableRow hover key={row.id}>
                <TableCell>
                  <Tooltip title={watchScoreTitle(row)} arrow>
                    <Chip size="small" label={row.importance} color={row.importance >= 80 ? 'success' : 'default'} />
                  </Tooltip>
                </TableCell>
                <TableCell>{row.watchType}</TableCell>
                <TableCell>{renderTeam(row.teamId, row.watchedTeam)}</TableCell>
                <TableCell>{row.achievementRank ? `#${row.achievementRank}` : '-'}</TableCell>
                <TableCell>{row.thresholdValue ?? '-'}</TableCell>
                <TableCell>
                  {row.leaderTeamName ? `${row.leaderTeamName} (${row.leaderValue ?? '-'})` : '-'}
                </TableCell>
                <TableCell>{renderMatch(row)}</TableCell>
                <TableCell>{renderLeague(row)}</TableCell>
                <TableCell>{formatDate(row.matchDate)}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default SocialPlanManager;
